// A public statement the app can judge (the Gossip's ability). A Storyteller would hear any
// sentence; the app offers sentences it can check, built from one or two players, a character,
// an alignment, a kind or a number. Anyone may make one a day, so making one gives nothing away.
import { useState } from "react";
import { useCommand } from "../hooks/useCommand";
import type { PlayerView, RoleCard, RoleType, Statement, Team } from "../protocol";
import { seatName } from "../state/selectors";
import { KINDS } from "./Characters";
import { Sheet } from "./Sheet";

type Kind = Statement["kind"];

type Field = "seat" | "other" | "role" | "alignment" | "type" | "count";

/** Each sentence, and what it is about (the server's `statements.FIELDS`). */
const SENTENCES: { kind: Kind; label: string; fields: Field[] }[] = [
  { kind: "is_role", label: "A player is a character", fields: ["seat", "role"] },
  { kind: "is_not_role", label: "A player is not a character", fields: ["seat", "role"] },
  { kind: "is_alignment", label: "A player is good or evil", fields: ["seat", "alignment"] },
  { kind: "is_type", label: "A player is a kind of character", fields: ["seat", "type"] },
  {
    kind: "either_role",
    label: "One of two players is a character",
    fields: ["seat", "other", "role"],
  },
  {
    kind: "either_alignment",
    label: "One of two players is good or evil",
    fields: ["seat", "other", "alignment"],
  },
  {
    kind: "either_type",
    label: "One of two players is a kind of character",
    fields: ["seat", "other", "type"],
  },
  { kind: "same_team", label: "Two players are on the same team", fields: ["seat", "other"] },
  {
    kind: "different_teams",
    label: "Two players are on different teams",
    fields: ["seat", "other"],
  },
  { kind: "in_play", label: "A character is in play", fields: ["role"] },
  { kind: "not_in_play", label: "A character is not in play", fields: ["role"] },
  { kind: "type_count", label: "How many of a kind are in play", fields: ["type", "count"] },
];

const ONE: Record<RoleType, string> = {
  townsfolk: "a Townsfolk",
  outsider: "an Outsider",
  minion: "a Minion",
  demon: "the Demon",
};
const NOUN: Record<RoleType, [string, string]> = {
  townsfolk: ["Townsfolk", "Townsfolk"],
  outsider: ["Outsider", "Outsiders"],
  minion: ["Minion", "Minions"],
  demon: ["Demon", "Demons"],
};

/** The sentence as every phone will show it (the server words it the same way). */
export function statementText(view: PlayerView, roles: Record<string, RoleCard>, s: Statement) {
  const who = seatName(view, s.seat_id);
  const other = seatName(view, s.other_seat_id);
  const role = s.role_id ? (roles[s.role_id]?.name ?? s.role_id) : "";
  const one = s.role_type ? ONE[s.role_type] : "";
  switch (s.kind) {
    case "is_role":
      return `${who} is the ${role}.`;
    case "is_not_role":
      return `${who} is not the ${role}.`;
    case "is_alignment":
      return `${who} is ${s.alignment}.`;
    case "is_type":
      return `${who} is ${one}.`;
    case "either_role":
      return `One of ${who} and ${other} is the ${role}.`;
    case "either_alignment":
      return `At least one of ${who} and ${other} is ${s.alignment}.`;
    case "either_type":
      return `At least one of ${who} and ${other} is ${one}.`;
    case "same_team":
      return `${who} and ${other} are on the same team.`;
    case "different_teams":
      return `${who} and ${other} are on different teams.`;
    case "in_play":
      return `The ${role} is in play.`;
    case "not_in_play":
      return `The ${role} is not in play.`;
    case "type_count": {
      const count = s.count ?? 0;
      const noun = s.role_type ? NOUN[s.role_type][count === 1 ? 0 : 1] : "";
      return `There ${count === 1 ? "is" : "are"} exactly ${count} ${noun} in play.`;
    }
  }
}

export function StatementSheet({
  open,
  onClose,
  view,
  roles,
}: {
  open: boolean;
  onClose: () => void;
  view: PlayerView;
  roles: Record<string, RoleCard>;
}) {
  const { run, busy } = useCommand();
  const [kind, setKind] = useState<Kind>("is_role");
  const [seatId, setSeatId] = useState(view.seats[0]?.seat_id ?? "");
  const [otherId, setOtherId] = useState(view.seats[1]?.seat_id ?? "");
  const [roleId, setRoleId] = useState(view.roles[0]?.id ?? "");
  const [alignment, setAlignment] = useState<Team>("evil");
  const [roleType, setRoleType] = useState<RoleType>("demon");
  const [count, setCount] = useState(1);

  const fields = SENTENCES.find((s) => s.kind === kind)?.fields ?? [];
  const uses = (f: Field) => fields.includes(f);
  // the second player is never the first
  const others = view.seats.filter((s) => s.seat_id !== seatId);
  const other = others.some((s) => s.seat_id === otherId) ? otherId : (others[0]?.seat_id ?? "");
  const statement: Statement = {
    kind,
    seat_id: uses("seat") ? seatId : null,
    other_seat_id: uses("other") ? other : null,
    role_id: uses("role") ? roleId : null,
    alignment: uses("alignment") ? alignment : null,
    role_type: uses("type") ? roleType : null,
    count: uses("count") ? count : null,
  };
  const text = statementText(view, roles, statement);

  const say = async () => {
    const r = await run({ type: "make_statement", statement });
    if (r.kind === "ack") onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} label="Make a statement">
      <div className="stack-sm">
        <h3>Make a statement</h3>
        <p className="small muted">
          Say it out loud too. Everyone&apos;s phone shows it. Anyone may make one statement a day;
          only the Gossip&apos;s can make someone die tonight, and only if it is true.
        </p>
      </div>
      <div className="field">
        <label htmlFor="statement-kind">What you say</label>
        <select
          id="statement-kind"
          className="input"
          value={kind}
          onChange={(e) => setKind(e.target.value as Kind)}
        >
          {SENTENCES.map((s) => (
            <option key={s.kind} value={s.kind}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      {uses("seat") && (
        <div className="field">
          <label htmlFor="statement-player">Player</label>
          <select
            id="statement-player"
            className="input"
            value={seatId}
            onChange={(e) => setSeatId(e.target.value)}
          >
            {view.seats.map((s) => (
              <option key={s.seat_id} value={s.seat_id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {uses("other") && (
        <div className="field">
          <label htmlFor="statement-other-player">Second player</label>
          <select
            id="statement-other-player"
            className="input"
            value={other}
            onChange={(e) => setOtherId(e.target.value)}
          >
            {others.map((s) => (
              <option key={s.seat_id} value={s.seat_id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {uses("role") && (
        <div className="field">
          <label htmlFor="statement-character">Character</label>
          <select
            id="statement-character"
            className="input"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            {view.roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {uses("alignment") && (
        <div className="field">
          <label htmlFor="statement-alignment">Good or evil</label>
          <select
            id="statement-alignment"
            className="input"
            value={alignment}
            onChange={(e) => setAlignment(e.target.value as Team)}
          >
            <option value="good">Good</option>
            <option value="evil">Evil</option>
          </select>
        </div>
      )}
      {uses("type") && (
        <div className="field">
          <label htmlFor="statement-kind-of">Kind of character</label>
          <select
            id="statement-kind-of"
            className="input"
            value={roleType}
            onChange={(e) => setRoleType(e.target.value as RoleType)}
          >
            {KINDS.map((k) => (
              <option key={k.type} value={k.type}>
                {k.one}
              </option>
            ))}
          </select>
        </div>
      )}
      {uses("count") && (
        <div className="field">
          <label htmlFor="statement-count">How many</label>
          <select
            id="statement-count"
            className="input"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
          >
            {Array.from({ length: 10 }, (_, n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="card card-tight">
        <span className="eyebrow">You say</span>
        <p style={{ fontSize: "1.1rem" }}>“{text}”</p>
      </div>
      <div className="stack-sm">
        <button className="btn btn-primary" disabled={busy} onClick={say}>
          Make this statement
        </button>
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}
